"use client";

import { useState, useEffect } from "react";
import { isNavigatorDefined } from "@huub-web/utils";

import type { BrowserPermissionState } from "../types";

export const useMicrophonePermissions = () => {
  const [microphonePermissionState, setMicrophonePermissionState] =
    useState<BrowserPermissionState>("idle");
  const [microphoneError, setMicrophoneError] = useState<string | null>(null);

  const requestMicrophonePermissions = async (
    onGranted?: () => void,
    onDenied?: () => void,
  ) => {
    if (!isNavigatorDefined()) {
      return;
    }

    setMicrophonePermissionState("pending");
    setMicrophoneError(null);

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicrophonePermissionState("granted");
      onGranted?.();
    } catch (err) {
      setMicrophonePermissionState("denied");
      onDenied?.();
      setMicrophoneError(
        err instanceof Error ? err.message : "Unknown error occurred",
      );
    }
  };

  useEffect(() => {
    if (!isNavigatorDefined()) {
      return;
    }

    // Check if permissions were previously granted
    navigator.permissions
      ?.query({ name: "microphone" as PermissionName })
      .then((result) => {
        if (result.state === "granted") {
          setMicrophonePermissionState("granted");
        } else if (result.state === "denied") {
          setMicrophonePermissionState("denied");
        }
      })
      .catch(() => {
        // Handle cases where the Permissions API is unavailable
        setMicrophonePermissionState("idle");
      });
  }, []);

  return {
    microphonePermissionState,
    requestMicrophonePermissions,
    microphoneError,
  };
};
